/**
 * Validation Service
 * Contains reusable validation functions for form inputs
 */

// SSN Validation
export const validateSSN = {
  format: (value) => {
    // Remove all non-digits first
    const digits = value.replace(/\D/g, '');
    
    // Format with dashes as user types (e.g., XXX-XX-XXXX)
    if (digits.length > 0) {
      if (digits.length < 4) {
        return digits;
      } else if (digits.length < 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
      }
    }
    return '';
  },
  
  mask: (event) => {
    // Only allow digits and dashes
    const regex = /^[\d-]*$/;
    if (!regex.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab') {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (!value) {
      return { isValid: true, message: '' };
    }
    
    // Check if input contains any non-digit characters (excluding dashes)
    const invalidChars = value.replace(/-/g, '').replace(/\d/g, '');
    if (invalidChars.length > 0) {
      return {
        isValid: false,
        message: 'SSN can only contain numbers and dashes'
      };
    }
    
    // Remove dashes for validation
    const digits = value.replace(/\D/g, '');
    
    // If some digits entered but not complete
    if (digits.length > 0 && digits.length < 9) {
      return {
        isValid: false,
        message: 'SSN must be 9 digits'
      };
    }
    
    // Check if it's exactly 9 digits
    if (digits.length !== 9) {
      return {
        isValid: false,
        message: 'Social Security Number must be 9 digits'
      };
    }
    
    // Extract the three parts for specific validation
    const area = parseInt(digits.substring(0, 3), 10);  // First 3 digits
    const group = parseInt(digits.substring(3, 5), 10); // Middle 2 digits
    const serial = parseInt(digits.substring(5, 9), 10); // Last 4 digits
    
    // Rule 1: The first three digits cannot be 000, 666, or 900-999
    if (area === 0) {
      return {
        isValid: false,
        message: 'Invalid SSN: First 3 digits cannot be 000'
      };
    }
    
    if (area === 666) {
      return {
        isValid: false,
        message: 'Invalid SSN: First 3 digits cannot be 666'
      };
    }
    
    if (area >= 900 && area <= 999) {
      return {
        isValid: false,
        message: 'Invalid SSN: First 3 digits cannot be between 900-999'
      };
    }
    
    // Rule 2: The second group (2 digits) cannot be 00
    if (group === 0) {
      return {
        isValid: false,
        message: 'Invalid SSN: Middle 2 digits cannot be 00'
      };
    }
    
    // Rule 3: The final group (4 digits) cannot be 0000
    if (serial === 0) {
      return {
        isValid: false,
        message: 'Invalid SSN: Last 4 digits cannot be 0000'
      };
    }
    
    // Check for obviously invalid SSNs (123-45-6789, etc.)
    if (/^123-?45-?6789$/.test(value)) {
      return {
        isValid: false,
        message: 'This appears to be a test SSN (123-45-6789)'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  // Function to normalize before sending to backend
  normalize: (value) => {
    return value.replace(/\D/g, '');
  }
};

// Phone Number Validation
export const validatePhone = {
  format: (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as user types (e.g., (XXX) XXX-XXXX)
    if (digits.length > 0) {
      if (digits.length < 4) {
        return `(${digits}`;
      } else if (digits.length < 7) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      }
    }
    return '';
  },
  
  mask: (event) => {
    // Only allow digits and formatting characters
    const regex = /^[\d\(\)\s-]*$/;
    if (!regex.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab') {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (!value) {
      return { isValid: true, message: '' };
    }
    
    // Check if input contains any non-digit characters (excluding formatting chars)
    const invalidChars = value.replace(/[\d\(\)\s-]/g, '');
    if (invalidChars.length > 0) {
      return {
        isValid: false,
        message: 'Phone number can only contain numbers and formatting characters'
      };
    }
    
    // Remove non-digits for validation
    const digits = value.replace(/\D/g, '');
    
    // If some digits entered but not complete
    if (digits.length > 0 && digits.length < 10) {
      return {
        isValid: false,
        message: 'Phone number must be 10 digits'
      };
    }
    
    // Check if it's exactly 10 digits (US phone number)
    if (digits.length !== 10) {
      return {
        isValid: false,
        message: 'Phone number must be 10 digits'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  normalize: (value) => {
    return value.replace(/\D/g, '');
  }
};

// Email Validation
export const validateEmail = {
  validate: (value) => {
    // If empty, don't show an error yet
    if (!value) {
      return { isValid: true, message: '' };
    }
    
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,20}$/;
    
    if (!regex.test(value)) {
      return {
        isValid: false,
        message: 'Please enter a valid email address'
      };
    }
    
    return { isValid: true, message: '' };
  }
};

// Currency Validation
export const validateCurrency = {
  format: (value) => {
    // Remove non-digits/dots
    let sanitized = value.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      sanitized = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Format with commas
    const numberParts = sanitized.split('.');
    numberParts[0] = numberParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    return numberParts.join('.');
  },
  
  mask: (event) => {
    // Allow only digits, commas, and one decimal point
    const value = event.target.value;
    const decimalCount = (value.match(/\./g) || []).length;
    
    if (!/^[\d,.]$/.test(event.key) && 
        event.key !== 'Backspace' && 
        event.key !== 'Tab') {
      event.preventDefault();
    }
    
    // Prevent additional decimal points if one already exists
    if (event.key === '.' && decimalCount >= 1) {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (!value) {
      return { isValid: true, message: '' };
    }
    
    // Check for invalid characters
    const invalidChars = value.replace(/[\d,.\$]/g, '');
    if (invalidChars.length > 0) {
      return {
        isValid: false,
        message: 'Amount can only contain numbers, commas, and decimal points'
      };
    }
    
    // Remove commas for validation
    const sanitized = value.replace(/,/g, '');
    
    if (isNaN(parseFloat(sanitized))) {
      return {
        isValid: false,
        message: 'Please enter a valid amount'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  normalize: (value) => {
    return parseFloat(value.replace(/,/g, '')).toString();
  }
};

// ZIP Code Validation
export const validateZip = {
  format: (value) => {
    // Remove non-digits
    const sanitized = value.replace(/\D/g, '');
    
    // Limit to 5 digits
    return sanitized.slice(0, 5);
  },
  
  mask: (event) => {
    // Allow only digits
    if (!/^\d$/.test(event.key) && 
        event.key !== 'Backspace' && 
        event.key !== 'Tab' &&
        event.key !== 'Delete' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight') {
      event.preventDefault();
    }
    
    // Prevent entering more than 5 digits
    if (/^\d$/.test(event.key) && event.target.value.replace(/\D/g, '').length >= 5) {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (!value) {
      return { isValid: true, message: '' };
    }
    
    // Check for invalid characters
    const invalidChars = value.replace(/\d/g, '');
    if (invalidChars.length > 0) {
      return {
        isValid: false,
        message: 'ZIP code can only contain numbers'
      };
    }
    
    // Extract digits
    const digits = value;
    
    // Check if it's exactly 5 digits
    if (digits.length !== 5) {
      return {
        isValid: false,
        message: 'ZIP code must be 5 digits'
      };
    }
    
    // Check for all zeros (00000)
    if (/^0+$/.test(digits)) {
      return {
        isValid: false,
        message: 'Invalid ZIP code'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  normalize: (value) => {
    // Return just the digits for storage/API
    return value.replace(/[^\d]/g, '');
  }
};

// EIN Validation (Employer Identification Number)
export const validateEIN = {
  format: (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as XX-XXXXXXX
    if (digits.length > 2) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`;
    }
    
    return digits;
  },
  
  mask: (event) => {
    // Only allow digits and dash
    const regex = /^[\d-]*$/;
    if (!regex.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab') {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (!value) {
      return { isValid: true, message: '' };
    }
    
    // Check for invalid characters
    const invalidChars = value.replace(/[\d-]/g, '');
    if (invalidChars.length > 0) {
      return {
        isValid: false,
        message: 'EIN can only contain numbers and dashes'
      };
    }
    
    // Remove dashes for validation
    const digits = value.replace(/\D/g, '');
    
    // If some digits entered but not complete
    if (digits.length > 0 && digits.length < 9) {
      return {
        isValid: false,
        message: 'EIN must be 9 digits'
      };
    }
    
    // Check if it's exactly 9 digits
    if (digits.length !== 9) {
      return {
        isValid: false,
        message: 'EIN must be 9 digits (XX-XXXXXXX)'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  normalize: (value) => {
    return value.replace(/\D/g, '');
  }
};

// Date of Birth Validation (18+ years check)
export const validateDateOfBirth = {
  validate: (value) => {
    // If empty, don't show an error yet
    if (!value) {
      return { isValid: true, message: '' };
    }
    
    try {
      // Parse the date string (expects format YYYY-MM-DD from HTML date input)
      const dob = new Date(value);
      const today = new Date();
      
      // Check if date is valid
      if (isNaN(dob.getTime())) {
        return {
          isValid: false,
          message: 'Please enter a valid date'
        };
      }
      
      // Check if date is in the future
      if (dob > today) {
        return {
          isValid: false,
          message: 'Date of birth cannot be in the future'
        };
      }
      
      // Calculate age
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      
      // Adjust age if birthday hasn't occurred yet this year
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }
      
      // Check if person is at least 18 years old
      if (age < 18) {
        return {
          isValid: false,
          message: 'Owner must be at least 18 years old'
        };
      }
      
      return { isValid: true, message: '' };
    } catch (error) {
      console.error('Error validating date of birth:', error);
      return {
        isValid: false,
        message: 'Please enter a valid date'
      };
    }
  },
  
  normalize: (value) => {
    // Return the date in ISO format (YYYY-MM-DD)
    return value;
  }
};

// Business Start Date Validation
export const validateBusinessStartDate = {
  validate: (value) => {
    // If empty, don't show an error yet
    if (!value) {
      return { isValid: true, message: '' };
    }
    
    try {
      // Parse the date string (expects format YYYY-MM-DD from HTML date input)
      const startDate = new Date(value);
      const today = new Date();
      
      // Check if date is valid
      if (isNaN(startDate.getTime())) {
        return {
          isValid: false,
          message: 'Please enter a valid business start date'
        };
      }
      
      // Check if the year is reasonable (not too far in the past or future)
      const year = startDate.getFullYear();
      if (year < 1800 || year > 2100) {
        return {
          isValid: false,
          message: 'Please enter a valid year between 1800 and 2100'
        };
      }
      
      // Check if date is in the future
      if (startDate > today) {
        return {
          isValid: false,
          message: 'Business start date cannot be in the future'
        };
      }
      
      return { isValid: true, message: '' };
    } catch (error) {
      console.error('Error validating business start date:', error);
      return {
        isValid: false,
        message: 'Please enter a valid date'
      };
    }
  },
  
  normalize: (value) => {
    // Return the date in ISO format (YYYY-MM-DD)
    return value;
  }
};

// State Validation
export const validateState = {
  validate: (value) => {
    // If value is null, undefined or empty string, show an error
    if (value === null || value === undefined || value === '' || (typeof value === 'string' && value.trim() === '')) {
      return { 
        isValid: false, 
        message: 'Please select a state' 
      };
    }
    
    // Check if the value is the default "Select a state" text
    if (typeof value === 'string' && value.toLowerCase() === 'select a state') {
      return { 
        isValid: false, 
        message: 'Please select a state' 
      };
    }
    
    // If it's a valid state code (2 letters), it's valid
    if (typeof value === 'string' && /^[A-Z]{2}$/.test(value)) {
      return { isValid: true, message: '' };
    }
    
    // Otherwise it's not a valid state code
    return { 
      isValid: false, 
      message: 'Please select a valid state' 
    };
  },
  
  normalize: (value) => {
    // Return the state code as is
    return value;
  }
};

// SIC Code Validation (Standard Industrial Classification)
export const validateSicCode = {
  format: (value) => {
    // Remove any non-digit characters
    return value.replace(/\D/g, '').substring(0, 4);
  },
  
  mask: (event) => {
    // Only allow digits
    const regex = /^\d$/;
    if (!regex.test(event.key) && 
        event.key !== 'Backspace' && 
        event.key !== 'Tab' &&
        event.key !== 'Delete' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight') {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (!value) {
      return { isValid: true, message: '' };
    }
    
    // Check for non-digit characters
    if (!/^\d+$/.test(value)) {
      return {
        isValid: false,
        message: 'SIC Code can only contain numbers'
      };
    }
    
    // Check if it's exactly 4 digits
    if (value.length !== 4) {
      return {
        isValid: false,
        message: 'SIC Code must be exactly 4 digits'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  normalize: (value) => {
    return value.replace(/\D/g, '').substring(0, 4);
  }
};

// Ownership Percentage Validation
export const validateOwnershipPercentage = {
  format: (value) => {
    // Remove any non-digits or dots
    let sanitized = value.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      sanitized = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Don't allow more than 3 digits (max 100)
    if (parts[0].length > 3) {
      parts[0] = parts[0].substring(0, 3);
    }
    
    // If the value is greater than 100, cap it at 100
    const numValue = parseFloat(sanitized);
    if (!isNaN(numValue) && numValue > 100) {
      return '100';
    }
    
    return sanitized;
  },
  
  mask: (event) => {
    // Only allow digits and a single decimal point
    if (!/[\d.]/.test(event.key) && 
        event.key !== 'Backspace' && 
        event.key !== 'Tab' &&
        event.key !== 'Delete' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight') {
      event.preventDefault();
    }
    
    // Prevent multiple decimal points
    if (event.key === '.' && event.target.value.includes('.')) {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (!value) {
      return { isValid: true, message: '' };
    }
    
    // Check if input is a valid number
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return {
        isValid: false,
        message: 'Ownership percentage must be a valid number'
      };
    }
    
    // Check if it's negative
    if (numValue < 0) {
      return {
        isValid: false,
        message: 'Ownership percentage cannot be negative'
      };
    }
    
    // Check if it's zero
    if (numValue === 0) {
      return {
        isValid: false,
        message: 'Ownership percentage must be greater than 0'
      };
    }
    
    // Check if it's greater than 100
    if (numValue > 100) {
      return {
        isValid: false,
        message: 'Ownership percentage cannot exceed 100%'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  // New method to validate combined ownership percentages
  validateCombined: (primaryValue, secondaryValue) => {
    // Helper to safely parse percentage, defaulting to 0 if invalid/empty
    const safeParsePercentage = (value) => {
      if (value === null || value === undefined || String(value).trim() === '') {
        return 0;
      }
      const num = parseFloat(value);
      // Return 0 if NaN, otherwise return the number
      return isNaN(num) ? 0 : num; 
    };

    const primaryPercentage = safeParsePercentage(primaryValue);
    const secondaryPercentage = safeParsePercentage(secondaryValue);
    
    // Check if the combined percentage exceeds 100%
    // Perform this check regardless of individual validity, as the sum is the focus here.
    const combinedPercentage = primaryPercentage + secondaryPercentage;
    if (combinedPercentage > 100) {
      return {
        isValid: false,
        // Use the specific error message requested
        message: 'Ownership percentage cannot exceed 100 between all of the owners' 
      };
    }
    
    // If the combined total is not over 100, this specific validation passes.
    // Individual field validation (like checking for negative numbers or > 100 individually)
    // is handled by the main validate function.
    return { isValid: true, message: '' };
  },
  
  normalize: (value) => {
    // Return the numeric value as a string
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    if (numValue > 100) return '100';
    return numValue.toString();
  }
};

// Street Address Validation
export const validateStreetAddress = {
  // format: (value) => {
  //   // Basic formatting: trim extra spaces
  //   return value.replace(/\s+/g, ' ').trim();
  // },
  
  mask: (event) => {
    // Allow most printable characters for addresses including spaces
    // We only block unsafe characters
    const blockedChars = /[<>{}[\]\\^~|]/;
    
    // Only block unsafe characters, explicitly allow spaces
    if (blockedChars.test(event.key) && 
        event.key !== ' ' &&  // Explicitly allow spaces
        event.key !== 'Backspace' && 
        event.key !== 'Tab' &&
        event.key !== 'Delete' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight') {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (!value) {
      return { isValid: true, message: '' };
    }
    
    // Basic address validation for US
    
    // Check for minimum length
    if (value.length < 3) {
      return {
        isValid: false,
        message: 'Address is too short'
      };
    }
    
    // Check for invalid characters
    const invalidChars = /[<>{}[\]\\^~|]/;
    if (invalidChars.test(value)) {
      return {
        isValid: false,
        message: 'Address contains invalid characters'
      };
    }
    
    // Check for just a single number (likely incomplete address)
    if (/^\d+$/.test(value)) {
      return {
        isValid: false,
        message: 'Please enter a complete street address'
      };
    }
    
    // Check for PO Box format (some lenders may not accept)
    const poBoxPattern = /^\s*p\.?\s*o\.?\s*box\s+\d+\s*$/i;
    if (poBoxPattern.test(value)) {
      return {
        isValid: true, // Still valid, but with a warning
        message: 'PO Box addresses may not be accepted by all lenders',
        isWarning: true
      };
    }
    
    // Should have a house/building number followed by a street name
    // Updated to support numeric street names like "177th St"
    const hasNumberAndStreet = /\d+\s+\S+/;
    if (!hasNumberAndStreet.test(value)) {
      return {
        isValid: false,
        message: 'Please include a building number and street name'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  normalize: (value) => {
    // Standardize spacing and capitalization for storage
    if (!value) return '';
    
    // Trim extra spaces
    let normalized = value.replace(/\s+/g, ' ').trim();
    
    // Capitalize first letter of each word
    normalized = normalized.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    
    // Handle specific abbreviations like St. or Ave.
    const abbreviations = {
      ' St ': ' Street ',
      ' St.': ' Street',
      ' Ave ': ' Avenue ',
      ' Ave.': ' Avenue',
      ' Rd ': ' Road ',
      ' Rd.': ' Road',
      ' Blvd ': ' Boulevard ',
      ' Blvd.': ' Boulevard',
      ' Ln ': ' Lane ',
      ' Ln.': ' Lane',
      ' Dr ': ' Drive ',
      ' Dr.': ' Drive',
      ' Ct ': ' Court ',
      ' Ct.': ' Court',
      ' Apt ': ' Apartment ',
      ' Apt.': ' Apartment',
      ' Ste ': ' Suite ',
      ' Ste.': ' Suite',
      ' Unit ': ' Unit ',
      ' #': ' #'
    };
    
    Object.entries(abbreviations).forEach(([abbr, full]) => {
      // Use word boundaries to avoid partial replacements
      const regex = new RegExp(`\\b${abbr.trim()}\\b`, 'gi');
      normalized = normalized.replace(regex, full);
    });
    
    return normalized;
  }
};

// City Name Validation
export const validateCity = {
  format: (value) => {
    // Basic formatting: trim extra spaces and capitalize each word
    return value
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());
  },
  
  mask: (event) => {
    // City names should only contain letters, spaces, hyphens, apostrophes, and periods
    const validChars = /^[a-zA-Z\s\-'.]+$/;
    if (!validChars.test(event.key) &&
        event.key !== 'Backspace' && 
        event.key !== 'Tab' &&
        event.key !== 'Delete' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight') {
      event.preventDefault();
    }
  },
  
  validate: (value) => {
    // If empty, don't show an error yet
    if (!value) {
      return { isValid: true, message: '' };
    }
    
    // Check for minimum length
    if (value.length < 2) {
      return {
        isValid: false,
        message: 'City name is too short'
      };
    }
    
    // City names should only contain letters, spaces, hyphens, apostrophes, and periods
    const validChars = /^[a-zA-Z\s\-'.]+$/;
    if (!validChars.test(value)) {
      return {
        isValid: false,
        message: 'City name can only contain letters, spaces, hyphens, apostrophes, and periods'
      };
    }
    
    // Check for numeric values
    if (/\d/.test(value)) {
      return {
        isValid: false,
        message: 'City name should not contain numbers'
      };
    }
    
    // Check for reasonable length
    if (value.length > 50) {
      return {
        isValid: false,
        message: 'City name is too long'
      };
    }
    
    return { isValid: true, message: '' };
  },
  
  normalize: (value) => {
    if (!value) return '';
    
    // Trim extra spaces and capitalize each word
    let normalized = value.replace(/\s+/g, ' ').trim()
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());
    
    // Handle common city name issues (Saint vs St, Fort vs Ft)
    const replacements = {
      'St ': 'Saint ',
      'St. ': 'Saint ',
      'St.': 'Saint',
      'Ft ': 'Fort ',
      'Ft. ': 'Fort ',
      'Ft.': 'Fort',
      'Mt ': 'Mount ',
      'Mt. ': 'Mount ',
      'Mt.': 'Mount'
    };
    
    Object.entries(replacements).forEach(([abbr, full]) => {
      if (normalized.startsWith(abbr)) {
        normalized = full + normalized.substring(abbr.length);
      }
    });
    
    return normalized;
  }
};

// Allow this list to grow as needed by adding new validation functions
// When adding new validators, follow the pattern of including:
// - format: function to format input as user types
// - mask: function to restrict input characters
// - validate: function to check if the final value is valid
// - normalize: function to convert to the format needed for backend 